import React, { useState } from 'react';
import { Handle, Position } from 'reactflow';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import AltRouteRoundedIcon from '@mui/icons-material/AltRouteRounded';
import InputRoundedIcon from '@mui/icons-material/InputRounded';
import ApiRoundedIcon from '@mui/icons-material/ApiRounded';
import StorageRoundedIcon from '@mui/icons-material/StorageRounded';
import CodeRoundedIcon from '@mui/icons-material/CodeRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import AlternateEmailRoundedIcon from '@mui/icons-material/AlternateEmailRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import TagRoundedIcon from '@mui/icons-material/TagRounded';
import ListAltRoundedIcon from '@mui/icons-material/ListAltRounded';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import LayersRoundedIcon from '@mui/icons-material/LayersRounded';
import TranslateRoundedIcon from '@mui/icons-material/TranslateRounded';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import { findFlowNodeItem } from './flowNodeCatalog';
import NodeCard from '../journey-studio/nodes/shared/NodeCard';
import NodeHeader from '../journey-studio/nodes/shared/NodeHeader';
import NodeFooter from '../journey-studio/nodes/shared/NodeFooter';
import { nodeTheme as sharedNodeTheme } from '../journey-studio/nodes/shared/nodeTheme';

/* Node preview surfaces are shared design tokens; keep execution and routing metadata untouched. */
const legacyNodeTheme = {
  start: {
    accentGradient: 'from-emerald-500 via-emerald-400 to-teal-400',
    border: 'border-emerald-200/90 dark:border-emerald-500/30',
    borderGlow: 'hover:border-emerald-400 dark:hover:border-emerald-400',
    selectedRing: 'ring-2 ring-emerald-500 shadow-[0_0_24px_rgba(16,185,129,0.25)]',
    iconBox: 'bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200/80 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-500/30 shadow-sm shadow-emerald-500/10',
    headerCategory: 'text-emerald-600 dark:text-emerald-400',
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-500/30',
    surface: 'border-emerald-100/90 bg-emerald-50/50 text-emerald-950 dark:border-emerald-500/20 dark:bg-emerald-950/20 dark:text-emerald-100',
    connector: 'border-emerald-300 text-emerald-600 bg-white dark:border-emerald-500/30 dark:text-emerald-300 dark:bg-slate-900',
    accentColor: '#10b981',
  },
  end: {
    accentGradient: 'from-rose-500 via-rose-400 to-pink-500',
    border: 'border-rose-200/90 dark:border-rose-500/30',
    borderGlow: 'hover:border-rose-400 dark:hover:border-rose-400',
    selectedRing: 'ring-2 ring-rose-500 shadow-[0_0_24px_rgba(244,63,94,0.25)]',
    iconBox: 'bg-rose-50 text-rose-600 ring-1 ring-rose-200/80 dark:bg-rose-950/40 dark:text-rose-300 dark:ring-rose-500/30 shadow-sm shadow-rose-500/10',
    headerCategory: 'text-rose-600 dark:text-rose-400',
    badge: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-500/30',
    surface: 'border-rose-100/90 bg-rose-50/50 text-rose-950 dark:border-rose-500/20 dark:bg-rose-950/20 dark:text-rose-100',
    connector: 'border-rose-300 text-rose-600 bg-white dark:border-rose-500/30 dark:text-rose-300 dark:bg-slate-900',
    accentColor: '#f43f5e',
  },
  message: {
    accentGradient: 'from-sky-500 via-blue-500 to-indigo-500',
    border: 'border-sky-200/90 dark:border-sky-500/30',
    borderGlow: 'hover:border-sky-400 dark:hover:border-sky-400',
    selectedRing: 'ring-2 ring-sky-500 shadow-[0_0_24px_rgba(14,165,233,0.25)]',
    iconBox: 'bg-sky-50 text-sky-600 ring-1 ring-sky-200/80 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-500/30 shadow-sm shadow-sky-500/10',
    headerCategory: 'text-sky-600 dark:text-sky-400',
    badge: 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-500/30',
    surface: 'border-slate-200/80 bg-slate-50/60 text-slate-800 dark:border-slate-800 dark:bg-slate-800/40 dark:text-slate-200',
    connector: 'border-sky-300 text-sky-600 bg-white dark:border-sky-500/30 dark:text-sky-300 dark:bg-slate-900',
    accentColor: '#0ea5e9',
  },
  condition: {
    accentGradient: 'from-violet-500 via-purple-500 to-indigo-600',
    border: 'border-violet-200/90 dark:border-violet-500/30',
    borderGlow: 'hover:border-violet-400 dark:hover:border-violet-400',
    selectedRing: 'ring-2 ring-violet-500 shadow-[0_0_24px_rgba(139,92,246,0.25)]',
    iconBox: 'bg-violet-50 text-violet-600 ring-1 ring-violet-200/80 dark:bg-violet-950/40 dark:text-violet-300 dark:ring-violet-500/30 shadow-sm shadow-violet-500/10',
    headerCategory: 'text-violet-600 dark:text-violet-400',
    badge: 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950/40 dark:text-violet-300 dark:border-violet-500/30',
    surface: 'border-violet-100/90 bg-violet-50/50 text-slate-800 dark:border-violet-500/20 dark:bg-violet-950/20 dark:text-slate-200',
    connector: 'border-violet-300 text-violet-600 bg-white dark:border-violet-500/30 dark:text-violet-300 dark:bg-slate-900',
    accentColor: '#8b5cf6',
  },
  input: {
    accentGradient: 'from-amber-500 via-amber-400 to-orange-400',
    border: 'border-amber-200/90 dark:border-amber-500/30',
    borderGlow: 'hover:border-amber-400 dark:hover:border-amber-400',
    selectedRing: 'ring-2 ring-amber-500 shadow-[0_0_24px_rgba(245,158,11,0.25)]',
    iconBox: 'bg-amber-50 text-amber-600 ring-1 ring-amber-200/80 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-500/30 shadow-sm shadow-amber-500/10',
    headerCategory: 'text-amber-600 dark:text-amber-400',
    badge: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-500/30',
    surface: 'border-amber-100/90 bg-amber-50/50 text-slate-800 dark:border-amber-500/20 dark:bg-amber-950/20 dark:text-slate-200',
    connector: 'border-amber-300 text-amber-700 bg-white dark:border-amber-500/30 dark:text-amber-300 dark:bg-slate-900',
    accentColor: '#f59e0b',
  },
  api_call: {
    accentGradient: 'from-cyan-500 via-teal-500 to-blue-600',
    border: 'border-cyan-200/90 dark:border-cyan-500/30',
    borderGlow: 'hover:border-cyan-400 dark:hover:border-cyan-400',
    selectedRing: 'ring-2 ring-cyan-500 shadow-[0_0_24px_rgba(6,182,212,0.25)]',
    iconBox: 'bg-cyan-50 text-cyan-600 ring-1 ring-cyan-200/80 dark:bg-cyan-950/40 dark:text-cyan-300 dark:ring-cyan-500/30 shadow-sm shadow-cyan-500/10',
    headerCategory: 'text-cyan-600 dark:text-cyan-400',
    badge: 'bg-cyan-50 text-cyan-700 border-cyan-200 dark:bg-cyan-950/40 dark:text-cyan-300 dark:border-cyan-500/30',
    surface: 'border-cyan-100/90 bg-cyan-50/50 text-slate-800 dark:border-cyan-500/20 dark:bg-cyan-950/20 dark:text-slate-200',
    connector: 'border-cyan-300 text-cyan-700 bg-white dark:border-cyan-500/30 dark:text-cyan-300 dark:bg-slate-900',
    accentColor: '#06b6d4',
  },
};

const baseIcons = {
  start: PlayArrowRoundedIcon,
  end: StopRoundedIcon,
  message: ChatBubbleOutlineRoundedIcon,
  condition: AltRouteRoundedIcon,
  input: InputRoundedIcon,
  api_call: ApiRoundedIcon,
};

/** Purpose: Bound the length of node preview text. Why: Long values must not expand canvas cards. */
const truncate = (value, limit = 92) => {
  if (!value) return '';
  return value.length > limit ? `${value.slice(0, limit - 1)}...` : value;
};

/** Purpose: Trim a display value with an optional fallback. Why: Blank fields need predictable preview text. */
const compactText = (value, fallback = '') => {
  const next = String(value || fallback || '').trim();
  return next;
};

/** Purpose: Choose labels, icons, and styles for input types. Why: Prompt validation types must be identifiable on the canvas. */
const getInputTypeMeta = (libraryKey, validation = '') => {
  const key = String(libraryKey || '').toLowerCase();
  const val = String(validation || '').toLowerCase();

  if (key === 'email' || val === 'email') {
    return {
      label: 'Email Input',
      icon: AlternateEmailRoundedIcon,
      badge: 'border-amber-200/90 bg-amber-50 text-amber-700 dark:border-amber-400/20 dark:bg-amber-500/10 dark:text-amber-300',
    };
  }
  if (key === 'phone' || val === 'phone') {
    return {
      label: 'Phone Input',
      icon: PhoneRoundedIcon,
      badge: 'border-blue-200/90 bg-blue-50 text-blue-700 dark:border-blue-400/20 dark:bg-blue-500/10 dark:text-blue-300',
    };
  }
  if (key === 'number' || val === 'number') {
    return {
      label: 'Number Input',
      icon: TagRoundedIcon,
      badge: 'border-emerald-200/90 bg-emerald-50 text-emerald-700 dark:border-emerald-400/20 dark:bg-emerald-500/10 dark:text-emerald-300',
    };
  }
  if (key === 'date' || val === 'date') {
    return {
      label: 'Date Picker',
      icon: CalendarTodayRoundedIcon,
      badge: 'border-purple-200/90 bg-purple-50 text-purple-700 dark:border-purple-400/20 dark:bg-purple-500/10 dark:text-purple-300',
    };
  }
  if (key === 'quick_replies' || val === 'quick_reply') {
    return {
      label: 'Choice Options',
      icon: ListAltRoundedIcon,
      badge: 'border-violet-200/90 bg-violet-50 text-violet-700 dark:border-violet-400/20 dark:bg-violet-500/10 dark:text-violet-300',
    };
  }
  if (key === 'file' || key === 'attachment') {
    return {
      label: 'File Upload',
      icon: AttachFileRoundedIcon,
      badge: 'border-cyan-200/90 bg-cyan-50 text-cyan-700 dark:border-cyan-400/20 dark:bg-cyan-500/10 dark:text-cyan-300',
    };
  }
  return {
    label: 'Text Prompt',
    icon: HelpOutlineRoundedIcon,
    badge: 'border-amber-200/90 bg-amber-50 text-amber-700 dark:border-amber-400/20 dark:bg-amber-500/10 dark:text-amber-300',
  };
};

/** Purpose: Choose the request-method badge. Why: API cards should expose their HTTP method at a glance. */
const getHttpMethodMeta = (method = 'POST') => {
  const m = String(method || 'POST').toUpperCase();
  switch (m) {
    case 'GET':
      return {
        method: 'GET',
        badge: 'border-emerald-300/80 bg-emerald-100 text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/20 dark:text-emerald-300',
      };
    case 'POST':
      return {
        method: 'POST',
        badge: 'border-sky-300/80 bg-sky-100 text-sky-800 dark:border-sky-500/30 dark:bg-sky-500/20 dark:text-sky-300',
      };
    case 'PUT':
      return {
        method: 'PUT',
        badge: 'border-amber-300/80 bg-amber-100 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/20 dark:text-amber-300',
      };
    case 'DELETE':
      return {
        method: 'DELETE',
        badge: 'border-rose-300/80 bg-rose-100 text-rose-800 dark:border-rose-500/30 dark:bg-rose-500/20 dark:text-rose-300',
      };
    case 'PATCH':
      return {
        method: 'PATCH',
        badge: 'border-purple-300/80 bg-purple-100 text-purple-800 dark:border-purple-500/30 dark:bg-purple-500/20 dark:text-purple-300',
      };
    default:
      return {
        method: m,
        badge: 'border-slate-300/80 bg-slate-100 text-slate-800 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300',
      };
  }
};

/** Purpose: provide shared chrome and controls for flow-canvas nodes. Why: consistent node behavior and styling should not be reimplemented for each node type. */
function NodeShell({
  type,
  selected,
  data,
  children,
  allowIncoming = true,
  allowOutgoing = true,
  hideOutgoing = false,
  compact = false,
  eyebrowText,
  badgeText,
}) {
  const theme = sharedNodeTheme[type] || sharedNodeTheme.message;
  const [expanded, setExpanded] = useState(false);

  const fullText = data?.config?.message || data?.config?.prompt || '';
  const item = findFlowNodeItem(data?.libraryKey);
  const Icon = item?.icon || baseIcons[type] || ChatBubbleOutlineRoundedIcon;
  const categoryLabel = eyebrowText || item?.label || data?.category || type;

  return (
    <NodeCard type={type} selected={selected} data={data} compact={compact}>

      {/* Target input handle (Left) */}
      {allowIncoming ? (
        <Handle
          type="target"
          position={Position.Left}
          title="Input connection point"
          className="nodrag nopan !left-0 !z-20 !h-4 !w-4 !-translate-x-1/2 !rounded-full !border-[2.5px] !border-white !bg-slate-300 !opacity-100 !shadow-[0_2px_8px_rgba(15,23,42,0.2)] !transition-all duration-150 hover:!scale-125 hover:!bg-indigo-500 hover:!ring-4 hover:!ring-indigo-500/20 dark:!border-slate-900 dark:!bg-slate-600"
        />
      ) : (
        <div className="absolute left-0 top-1/2 z-20 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-slate-300/80 shadow-[0_2px_6px_rgba(15,23,42,0.12)] dark:border-slate-900 dark:bg-slate-600" />
      )}

      {/* Default outgoing handle (Right) */}
      {allowOutgoing ? (
        <Handle
          type="source"
          position={Position.Right}
          onClick={(event) => {
            event.stopPropagation();
            data?.onOpenNodePicker?.({ sourceNodeId: data?.nodeId || data?.id, sourceHandle: 'next' });
          }}
          title="Click to add next step, or drag to connect this node"
          className={[
            'flow-connection-handle nodrag nopan !right-0 !z-30 !w-10 !border-0 !bg-transparent !opacity-100 !shadow-none',
            theme.connector,
          ].join(' ')}
        />
      ) : !hideOutgoing ? (
        <div className="absolute right-0 top-1/2 z-20 h-3 w-3 translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-slate-300/80 shadow-[0_2px_6px_rgba(15,23,42,0.12)] dark:border-slate-900 dark:bg-slate-600" />
      ) : null}

      <NodeHeader type={type} data={data} Icon={Icon} categoryLabel={categoryLabel} compact={compact} />

      {/* Main Node Body / Slot */}
      {children}
      {data?.previewKey && <div className="mt-2 break-all text-[10px] text-sky-600" title="Mapped localization key">🌐 {data.previewKey}</div>}
      {fullText.length > 90 && <div className="mt-2">
        <button className="nodrag text-[11px] font-semibold text-sky-600" onClick={() => setExpanded(!expanded)}>{expanded ? 'Collapse text' : 'Read full text'}</button>
        {expanded && <div className="nodrag nowheel mt-1 max-h-60 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-slate-50 p-2 text-xs dark:bg-slate-800">{fullText}</div>}
      </div>}
      {data?.editorWarnings?.map((warning) => <button key={warning} onClick={data.onInspect} className="nodrag mt-2 block text-left text-[10px] font-semibold text-amber-700" title="Open node settings">⚠ {warning}</button>)}

      {/* Incoming Quick Reply Breadcrumb Badges */}
      {Array.isArray(data?.incomingQuickReplyRoutes) && data.incomingQuickReplyRoutes.length > 0 ? (
        <div className="mt-3 flex flex-wrap items-center gap-1.5 rounded-xl border border-violet-200/80 bg-violet-50/70 p-2 text-[10px] text-violet-900 dark:border-violet-500/20 dark:bg-violet-950/30 dark:text-violet-200">
          <span className="flex items-center gap-1 font-bold uppercase tracking-[0.1em] text-violet-700 dark:text-violet-300">
            <AltRouteRoundedIcon sx={{ fontSize: 12 }} />
            Routed via
          </span>
          {data.incomingQuickReplyRoutes.map((route, index) => (
            <span
              key={`${route.sourceTitle}-${route.option}-${index}`}
              className={`rounded-md px-1.5 py-0.5 font-semibold shadow-xs ${
                String(route.option).toLowerCase() === 'fallback'
                  ? 'border border-rose-200 bg-rose-100 text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/20 dark:text-rose-200'
                  : 'border border-violet-200/60 bg-white text-violet-800 dark:border-white/10 dark:bg-slate-900 dark:text-violet-200'
              }`}
            >
              {truncate(route.option, 18)}
            </span>
          ))}
        </div>
      ) : null}

      {/* Drop-off Heatmap Analytics Badge */}
      {data?.analytics ? (
        <div className="mt-2.5 flex items-center justify-between rounded-xl border border-sky-200/90 bg-gradient-to-r from-sky-50/90 via-indigo-50/70 to-purple-50/90 p-2 text-[10px] shadow-2xs dark:border-sky-500/20 dark:bg-slate-800/90">
          <div className="flex items-center gap-1.5 font-bold text-sky-800 dark:text-sky-300">
            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            <span>{data.analytics.visits || 0} visits ({data.analytics.retention || '100%'})</span>
          </div>
          <div className={`rounded-md px-1.5 py-0.5 font-bold ${
            (data.analytics.dropOffCount || 0) > 0
              ? 'border border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/30 dark:bg-rose-950/40 dark:text-rose-300'
              : 'border border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-950/40 dark:text-emerald-300'
          }`}>
            {data.analytics.dropOffCount || 0} drop-offs ({data.analytics.dropOffRate || '0%'})
          </div>
        </div>
      ) : null}

      {/* Footer Info / Variable Badge */}
      <NodeFooter type={type} data={data} badgeText={badgeText} compact={compact} />
    </NodeCard>
  );
}

const branchPalette = [
  { dot: '#8b5cf6', row: 'border-violet-200/90 bg-violet-50/80 text-violet-900 hover:border-violet-400 dark:border-violet-500/25 dark:bg-violet-950/30 dark:text-violet-200 dark:hover:border-violet-400/50' },
  { dot: '#0ea5e9', row: 'border-sky-200/90 bg-sky-50/80 text-sky-900 hover:border-sky-400 dark:border-sky-500/25 dark:bg-sky-950/30 dark:text-sky-200 dark:hover:border-sky-400/50' },
  { dot: '#f59e0b', row: 'border-amber-200/90 bg-amber-50/80 text-amber-900 hover:border-amber-400 dark:border-amber-500/25 dark:bg-amber-950/30 dark:text-amber-200 dark:hover:border-amber-400/50' },
  { dot: '#10b981', row: 'border-emerald-200/90 bg-emerald-50/80 text-emerald-900 hover:border-emerald-400 dark:border-emerald-500/25 dark:bg-emerald-950/30 dark:text-emerald-200 dark:hover:border-emerald-400/50' },
  { dot: '#ec4899', row: 'border-pink-200/90 bg-pink-50/80 text-pink-900 hover:border-pink-400 dark:border-pink-500/25 dark:bg-pink-950/30 dark:text-pink-200 dark:hover:border-pink-400/50' },
];

/** Purpose: render selectable quick-reply handles on a node. Why: authors need explicit canvas ports to connect each answer to its next step. */
export function QuickReplyButtons({
  options = [],
  onOpenNodePicker,
  sourceNodeId,
  showFallback = false,
  fallbackConfigured = false,
  connectedSourceHandles = [],
  onBranchHover,
  onGoToBranch,
}) {
  const connected = new Set(connectedSourceHandles);
  const [showAll, setShowAll] = useState(false);

  return (
    <div className="flex flex-col gap-1.5">
      {options.length > 4 && <button className="nodrag text-left text-[10px] font-semibold text-slate-500" onClick={() => setShowAll(!showAll)}>{showAll ? 'Compact labels' : 'Expand option labels'}</button>}
      {options.map((option, index) => {
        const optionTitle = String(
          typeof option === 'object' && option !== null
            ? option.title ?? option.label ?? option.text ?? option.value ?? `Option ${index + 1}`
            : option ?? `Option ${index + 1}`
        );
        const handleId = `quick-reply-${index}`;
        const mappedKey = typeof option === 'object' && option !== null ? String(option.localizationKey || '').trim() : '';
        const palette = branchPalette[index % branchPalette.length];
        const isConnected = connected.has(handleId);

        return (
          <div
            key={handleId}
            onMouseEnter={() => onBranchHover?.(handleId)}
            onMouseLeave={() => onBranchHover?.(null)}
            className={[
              'group/route relative mr-[-10px] flex min-h-[30px] w-full items-center gap-2 rounded-lg border py-1 pl-2.5 pr-7 text-[12px] font-semibold shadow-xs transition-all duration-150 hover:shadow-md',
              palette.row,
            ].join(' ')}
          >
            <span className="grid h-5 w-5 shrink-0 place-items-center rounded-lg bg-white/90 text-[10px] font-black text-slate-700 shadow-xs dark:bg-slate-900 dark:text-slate-200">
              {index + 1}
            </span>
            <span className={`min-w-0 flex-1 ${showAll ? 'break-words [overflow-wrap:anywhere]' : 'truncate'}`} title={mappedKey || optionTitle}>
              {mappedKey ? `🌐 ${mappedKey}` : showAll ? optionTitle : truncate(optionTitle, 24)}
              {option?.previewKey && <span className="block truncate text-[9px] opacity-60" title={option.previewKey}>🌐 {option.previewKey}</span>}
            </span>
            {isConnected && onGoToBranch && <button aria-label={`Go to connected node for ${optionTitle}`} title="Go to connected node" className="nodrag nopan px-1" onClick={(event) => { event.stopPropagation(); onGoToBranch(handleId); }}>↗</button>}
            <span className={`flex items-center gap-0.5 text-[9px] font-bold ${isConnected ? 'text-emerald-600 dark:text-emerald-400' : 'opacity-60'}`}>
              {isConnected ? (
                <>
                  <CheckRoundedIcon sx={{ fontSize: 11 }} />
                  Linked
                </>
              ) : (
                <>
                  Route
                  <ArrowForwardRoundedIcon sx={{ fontSize: 10 }} />
                </>
              )}
            </span>
            <Handle
              id={handleId}
              type="source"
              position={Position.Right}
              title={`Drag to connect option "${optionTitle}"`}
              onClick={(event) => {
                event.stopPropagation();
                onOpenNodePicker?.({ sourceNodeId, sourceHandle: handleId });
              }}
              className="nodrag nopan !right-0 !h-[17px] !w-[17px] !translate-x-1/2 !rounded-full !border-[3px] !border-white !opacity-100 !shadow-[0_2px_8px_rgba(15,23,42,0.3)] !transition-transform hover:!scale-125 dark:!border-slate-900"
              style={{ backgroundColor: palette.dot }}
            />
          </div>
        );
      })}

      {showFallback ? (
        <div className="relative mr-[-10px] flex min-h-[38px] w-full items-center gap-2 rounded-xl border border-rose-200/90 bg-rose-50/70 py-2 pl-2.5 pr-7 text-[12px] font-semibold text-rose-800 shadow-xs transition-all duration-150 hover:-translate-y-px hover:shadow-md dark:border-rose-500/25 dark:bg-rose-950/30 dark:text-rose-200">
          <span className="grid h-5 w-5 shrink-0 place-items-center rounded-lg bg-white/90 text-[11px] font-bold text-rose-700 shadow-xs dark:bg-slate-900 dark:text-rose-300">
            ↳
          </span>
          <span className="min-w-0 flex-1 truncate">Fallback Route</span>
          <span className={`text-[9px] font-bold ${fallbackConfigured ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-500 dark:text-rose-400'}`}>
            {fallbackConfigured ? 'Linked' : 'Add path'}
          </span>
          <Handle
            id="quick-reply-fallback"
            type="source"
            position={Position.Right}
            title="Click to add or drag to connect fallback path"
            onClick={(event) => {
              event.stopPropagation();
              onOpenNodePicker?.({ sourceNodeId, sourceHandle: 'quick-reply-fallback' });
            }}
            className="nodrag nopan !right-0 !h-[17px] !w-[17px] !translate-x-1/2 !rounded-full !border-[3px] !border-rose-600 !bg-white !opacity-100 !shadow-[0_2px_8px_rgba(244,63,94,0.4)] !transition-transform hover:!scale-125 dark:!border-rose-400 dark:!bg-slate-900"
          />
        </div>
      ) : null}

      {showFallback && !fallbackConfigured ? (
        <span className="flex items-center gap-1 px-1 text-[10px] font-semibold text-rose-600 dark:text-rose-300">
          <ErrorOutlineRoundedIcon sx={{ fontSize: 13 }} />
          Fallback not connected yet
        </span>
      ) : null}
    </div>
  );
}

/** Purpose: render the entry node of a flow. Why: every journey needs a visible, fixed starting point on the canvas. */
export function StartNode({ data, selected, id }) {
  return (
    <NodeShell
      type="start"
      selected={selected}
      data={{ ...data, id, nodeId: id, category: 'Trigger' }}
      eyebrowText="Trigger · Entry Point"
      badgeText="Entry"
      allowIncoming={false}
    >
      <div className="rounded-xl border border-emerald-200/80 bg-emerald-50/60 p-3.5 text-[13px] leading-relaxed text-emerald-950 dark:border-emerald-500/20 dark:bg-emerald-950/20 dark:text-emerald-100">
        <div className="flex items-center gap-1.5 font-bold text-emerald-800 dark:text-emerald-200">
          <span className="h-2 w-2 rounded-full bg-emerald-500" />
          <span>Conversation Starts</span>
        </div>
        <p className="mt-1 text-[12px] leading-snug text-emerald-900/80 dark:text-emerald-200/80">
          {truncate(data?.description || 'First touchpoint when user opens the chat or starts the flow.')}
        </p>
      </div>
    </NodeShell>
  );
}

/** Purpose: render a terminal flow node. Why: authors need an explicit endpoint to represent journey completion. */
export function EndNode({ data, selected, id }) {
  return (
    <NodeShell
      type="end"
      selected={selected}
      data={{ ...data, id, nodeId: id, category: 'Completion' }}
      eyebrowText="Terminal · End Step"
      badgeText="End"
      allowOutgoing={false}
    >
      <div className="rounded-xl border border-rose-200/80 bg-rose-50/60 p-3.5 text-[13px] leading-relaxed text-rose-950 dark:border-rose-500/20 dark:bg-rose-950/20 dark:text-rose-100">
        <div className="flex items-center gap-1.5 font-bold text-rose-800 dark:text-rose-200">
          <span className="h-2 w-2 rounded-full bg-rose-500" />
          <span>Journey Closes</span>
        </div>
        <p className="mt-1 text-[12px] leading-snug text-rose-900/80 dark:text-rose-200/80">
          {truncate(data?.config?.message || data?.description || 'Ends the conversation or returns to the calling parent flow.')}
        </p>
      </div>
    </NodeShell>
  );
}

/** Purpose: render a bot message node and its summary. Why: authors need to inspect message content and connections directly on the canvas. */
export function MessageNode({ data, selected, id }) {
  const config = data?.config || {};
  const isMediaNode = ['image', 'video', 'file'].includes(data?.libraryKey) || ['image', 'video', 'file'].includes(config.mediaType) || Boolean(config.mediaUrl);
  const isDynamicNode = data?.libraryKey === 'dynamic_chat';
  const isQuickReplyNode = data?.libraryKey === 'message_quick_replies';
  const isListNode = ['message_whatsapp_list', 'message_carousel', 'product_catalogue', 'carousel_prompt'].includes(data?.libraryKey);
  const isChatHandoffNode = data?.libraryKey === 'chat_handoff';
  const isDetectLanguageNode = data?.libraryKey === 'detect_language' || config.uiNodeKey === 'detect_language';

  return (
    <NodeShell
      type="message"
      selected={selected}
      data={{ ...data, id, nodeId: id }}
      eyebrowText={isDynamicNode ? 'AI Agent · Dynamic Chat' : isChatHandoffNode ? 'Human Agent · Handoff' : isDetectLanguageNode ? 'Localization · Bharat Smart Detect' : isMediaNode ? 'Rich Media · Message' : 'Bot Message'}
      badgeText={isDynamicNode ? 'AI Chat' : isChatHandoffNode ? 'Live Agent' : isDetectLanguageNode ? 'Smart Detect' : isMediaNode ? 'Media' : 'Message'}
      allowOutgoing={!isQuickReplyNode && !isChatHandoffNode && !isDynamicNode}
      hideOutgoing={isQuickReplyNode || isChatHandoffNode || isDynamicNode}
    >
      <div className={['min-h-[105px] space-y-3 rounded-xl border p-3.5 text-[13px] leading-relaxed', legacyNodeTheme.message.surface].join(' ')}>
        {isMediaNode ? (
          <>
            <div className="overflow-hidden rounded-xl border border-slate-200/90 bg-slate-100/90 dark:border-white/10 dark:bg-slate-900/80">
              {config.mediaUrl ? (
                config.mediaType === 'video' ? (
                  <video src={config.mediaUrl} className="h-24 w-full object-cover" />
                ) : (
                  <img src={config.mediaUrl} alt={config.alt || config.label || 'Media preview'} className="h-24 w-full object-cover" />
                )
              ) : (
                <div className="flex h-20 flex-col items-center justify-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                  <AttachFileRoundedIcon sx={{ fontSize: 20 }} />
                  {config.mediaType || 'Media'} Preview
                </div>
              )}
            </div>
            <div className="text-slate-700 dark:text-slate-300">
              {truncate(config.message || 'Add caption for this media message.')}
            </div>
          </>
        ) : isChatHandoffNode ? (
          <>
            <div className="flex items-center gap-2 font-semibold text-slate-900 dark:text-slate-100">
              <SupportAgentRoundedIcon sx={{ fontSize: 18 }} className="text-sky-600 dark:text-sky-400" />
              <span>{truncate(config.message || 'Transferring conversation to a live human agent...', 75)}</span>
            </div>
            <div className="flex flex-col gap-2 pt-1">
              <div className="relative mr-[-10px] flex items-center justify-between rounded-xl border border-emerald-200/90 bg-emerald-50/80 py-2 pl-3 pr-7 text-[11px] font-bold text-emerald-800 shadow-xs dark:border-emerald-500/25 dark:bg-emerald-950/30 dark:text-emerald-200">
                <span className="flex items-center gap-1.5">
                  <CheckRoundedIcon sx={{ fontSize: 13 }} />
                  Agent Resolved
                </span>
                <span className="text-[9px] opacity-60">Path</span>
                <Handle
                  id="handoff-resolved"
                  type="source"
                  position={Position.Right}
                  title="Agent resolved path"
                  onClick={(event) => {
                    event.stopPropagation();
                    data?.onOpenNodePicker?.({ sourceNodeId: id, sourceHandle: 'handoff-resolved' });
                  }}
                  className="nodrag nopan !right-0 !h-[17px] !w-[17px] !translate-x-1/2 !rounded-full !border-[3px] !border-emerald-600 !bg-white !opacity-100 !shadow-[0_2px_8px_rgba(16,185,129,0.4)] !transition-transform hover:!scale-125 dark:!border-emerald-400 dark:!bg-slate-900"
                />
              </div>
              <div className="relative mr-[-10px] flex items-center justify-between rounded-xl border border-rose-200/90 bg-rose-50/80 py-2 pl-3 pr-7 text-[11px] font-bold text-rose-800 shadow-xs dark:border-rose-500/25 dark:bg-rose-950/30 dark:text-rose-200">
                <span className="flex items-center gap-1.5">
                  <ErrorOutlineRoundedIcon sx={{ fontSize: 13 }} />
                  Queue Timeout / Offline
                </span>
                <span className="text-[9px] opacity-60">Fallback</span>
                <Handle
                  id="handoff-timeout"
                  type="source"
                  position={Position.Right}
                  title="Queue timeout fallback path"
                  onClick={(event) => {
                    event.stopPropagation();
                    data?.onOpenNodePicker?.({ sourceNodeId: id, sourceHandle: 'handoff-timeout' });
                  }}
                  className="nodrag nopan !right-0 !h-[17px] !w-[17px] !translate-x-1/2 !rounded-full !border-[3px] !border-rose-600 !bg-white !opacity-100 !shadow-[0_2px_8px_rgba(244,63,94,0.4)] !transition-transform hover:!scale-125 dark:!border-rose-400 dark:!bg-slate-900"
                />
              </div>
            </div>
          </>
        ) : isQuickReplyNode ? (
          <>
            <div className="font-semibold text-slate-900 dark:text-slate-100">
              {config.localizationKey ? `🌐 ${config.localizationKey}` : truncate(config.message || config.prompt || 'Choose one of the options below:', 90)}
            </div>
            {/* Variable Storage Box */}
            <div className="flex items-center justify-between rounded-lg border border-amber-200/70 bg-amber-50/70 px-2.5 py-1.5 text-[11px] text-amber-900 dark:border-amber-500/20 dark:bg-amber-950/30 dark:text-amber-200">
              <span className="flex items-center gap-1 font-medium opacity-85">
                <CodeRoundedIcon sx={{ fontSize: 13 }} />
                Stores in:
              </span>
              <span className="font-mono font-bold text-amber-800 dark:text-amber-200">
                {`{{${config.variable || config.variableName || 'user_input'}}}`}
              </span>
            </div>
            <QuickReplyButtons
              options={config.options || ['Option 1', 'Option 2']}
              onOpenNodePicker={data?.onOpenNodePicker}
              sourceNodeId={id}
              showFallback
              fallbackConfigured={data?.quickReplyFallbackConfigured}
              connectedSourceHandles={data?.connectedSourceHandles}
              onBranchHover={data?.onBranchHover}
              onGoToBranch={data?.onGoToBranch}
            />
          </>
        ) : isListNode ? (
          <>
            <div className="font-semibold text-slate-900 dark:text-slate-100">
              {truncate(config.title || config.prompt || config.message || 'Choose from the list below')}
            </div>
            {(config.variable || config.variableName) ? (
              <div className="flex items-center justify-between rounded-lg border border-amber-200/70 bg-amber-50/70 px-2.5 py-1.5 text-[11px] text-amber-900 dark:border-amber-500/20 dark:bg-amber-950/30 dark:text-amber-200">
                <span className="flex items-center gap-1 font-medium opacity-85">
                  <CodeRoundedIcon sx={{ fontSize: 13 }} />
                  Stores in:
                </span>
                <span className="font-mono font-bold text-amber-800 dark:text-amber-200">
                  {`{{${config.variable || config.variableName}}}`}
                </span>
              </div>
            ) : null}
            <div className="space-y-1.5">
              {(config.listItems || []).slice(0, 2).map((item, index) => (
                <div key={`${item?.value || item?.label || 'item'}-${index}`} className="rounded-lg border border-slate-200/80 bg-white/80 px-2.5 py-2 shadow-2xs dark:border-white/10 dark:bg-slate-900/60">
                  <div className="text-[12px] font-bold text-slate-800 dark:text-slate-100">{truncate(item?.label || 'List option', 30)}</div>
                  {item?.description ? <div className="text-[10px] text-slate-500 dark:text-slate-400">{truncate(item.description, 34)}</div> : null}
                </div>
              ))}
              {(config.listItems || []).length === 0 ? (
                <div className="text-[11px] text-slate-500 dark:text-slate-400">Add list options in the right sidebar.</div>
              ) : null}
            </div>
          </>
        ) : isDynamicNode ? (
          <>
            <div className="flex items-center gap-1.5 rounded-lg border border-violet-200/80 bg-violet-50/70 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-violet-700 dark:border-violet-500/25 dark:bg-violet-950/40 dark:text-violet-300">
              <AutoAwesomeRoundedIcon sx={{ fontSize: 13 }} />
              {config.conversationMode ? 'Conversation · NLU paused' : 'AI Prompt Instruction'}
            </div>
            <div className="text-[12px] font-medium leading-relaxed text-slate-800 dark:text-slate-200">
              {truncate(config.prompt || config.message || 'Generate dynamic intelligent response with context.')}
            </div>
            <div className="flex flex-col gap-2 pt-1">
              {config.conversationMode && <div className="text-[10px] text-violet-700">{(config.collectFields || []).length} fields · {(config.workflowSkills || []).length} skills · {config.maxTurns || 40} turns max</div>}
              <div className="relative mr-[-10px] flex items-center justify-between rounded-xl border border-emerald-200/90 bg-emerald-50/80 py-2 pl-3 pr-7 text-[11px] font-bold text-emerald-800 shadow-xs dark:border-emerald-500/25 dark:bg-emerald-950/30 dark:text-emerald-200">
                <span className="flex items-center gap-1.5">
                  <CheckRoundedIcon sx={{ fontSize: 13 }} />
                  {config.conversationMode ? 'Goal completed' : 'AI Generated'}
                </span>
                <span className="text-[9px] opacity-60">Success</span>
                <Handle
                  id="success"
                  type="source"
                  position={Position.Right}
                  title="Drag to connect AI success path, or click to add next step"
                  onClick={(event) => {
                    event.stopPropagation();
                    data?.onOpenNodePicker?.({ sourceNodeId: id, sourceHandle: 'success' });
                  }}
                  className="nodrag nopan !right-0 !z-30 !h-[17px] !w-[17px] !translate-x-1/2 !rounded-full !border-[3px] !border-emerald-600 !bg-white !opacity-100 !shadow-[0_2px_8px_rgba(16,185,129,0.4)] !transition-transform hover:!scale-125 dark:!border-emerald-400 dark:!bg-slate-900"
                />
              </div>
              <div className="relative mr-[-10px] flex items-center justify-between rounded-xl border border-rose-200/90 bg-rose-50/80 py-2 pl-3 pr-7 text-[11px] font-bold text-rose-800 shadow-xs dark:border-rose-500/25 dark:bg-rose-950/30 dark:text-rose-200">
                <span className="flex items-center gap-1.5">
                  <ErrorOutlineRoundedIcon sx={{ fontSize: 13 }} />
                  Provider Failure
                </span>
                <span className="text-[9px] opacity-60">Fallback</span>
                <Handle
                  id="failure"
                  type="source"
                  position={Position.Right}
                  title="Drag to connect AI failure/fallback path, or click to add next step"
                  onClick={(event) => {
                    event.stopPropagation();
                    data?.onOpenNodePicker?.({ sourceNodeId: id, sourceHandle: 'failure' });
                  }}
                  className="nodrag nopan !right-0 !z-30 !h-[17px] !w-[17px] !translate-x-1/2 !rounded-full !border-[3px] !border-rose-600 !bg-white !opacity-100 !shadow-[0_2px_8px_rgba(244,63,94,0.4)] !transition-transform hover:!scale-125 dark:!border-rose-400 dark:!bg-slate-900"
                />
              </div>
              {config.conversationMode && ['exit', 'handoff'].map((branch) => <div key={branch} className="relative mr-[-10px] rounded-xl border border-violet-200 bg-violet-50 py-2 pl-3 pr-7 text-[11px] font-bold text-violet-800">
                {branch === 'exit' ? 'User exit' : 'Human assistance'}
                <Handle id={branch} type="source" position={Position.Right} title={`Connect ${branch} route`} onClick={(event) => { event.stopPropagation(); data?.onOpenNodePicker?.({ sourceNodeId: id, sourceHandle: branch }); }} className="nodrag nopan !right-0 !h-[17px] !w-[17px] !border-violet-500 !bg-white" />
              </div>)}
            </div>
          </>
        ) : isDetectLanguageNode ? (
          <>
            <div className="flex items-center gap-1.5 rounded-lg border border-teal-200/80 bg-teal-50/70 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-teal-700 dark:border-teal-500/25 dark:bg-teal-950/40 dark:text-teal-300">
              <TranslateRoundedIcon sx={{ fontSize: 13 }} />
              Auto Detect Indian Languages
            </div>
            <div className="text-[12px] font-medium leading-relaxed text-slate-800 dark:text-slate-200">
              Detects Devanagari (Hindi/Marathi), Gujarati, Bengali, Tamil, Telugu & Hinglish automatically.
            </div>
            <div className="flex items-center justify-between rounded-lg border border-teal-200/70 bg-teal-50/70 px-2.5 py-1.5 text-[11px] text-teal-900 dark:border-teal-500/20 dark:bg-teal-950/30 dark:text-teal-200">
              <span className="font-medium opacity-85">Stores in:</span>
              <span className="font-mono font-bold">{`{{${config.saveToVariable || 'detected_language'}}}`}</span>
            </div>
          </>
        ) : (data?.libraryKey === 'execute_flow' || config?.uiNodeKey === 'execute_flow' || data?.libraryKey === 'switch_flow') ? (
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5 rounded-lg border border-indigo-200/80 bg-indigo-50/70 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-indigo-700 dark:border-indigo-500/25 dark:bg-indigo-950/40 dark:text-indigo-300">
              <AccountTreeOutlinedIcon sx={{ fontSize: 13 }} />
              Execute Flow
            </div>
            {data?.targetFlowName ? (
              <div className="rounded-lg border border-indigo-200/70 bg-indigo-50/50 p-2 text-[12px] font-semibold text-indigo-950 dark:border-indigo-500/20 dark:bg-indigo-950/20 dark:text-indigo-200">
                <span className="text-[10px] uppercase tracking-wider text-indigo-500 block font-bold">Target journey</span>
                <span className="font-bold truncate block">{data.targetFlowName}</span>
              </div>
            ) : config.targetFlowId ? (
              <div className="rounded-lg border border-rose-300/80 bg-rose-50/90 p-2 text-[11px] text-rose-800 dark:border-rose-500/30 dark:bg-rose-950/30 dark:text-rose-200">
                <span className="font-bold flex items-center gap-1 text-rose-700 dark:text-rose-300">
                  <ErrorOutlineRoundedIcon sx={{ fontSize: 13 }} />
                  Target flow deleted/missing
                </span>
                <span className="font-mono text-[10px] truncate block opacity-80 mt-0.5">{String(config.targetFlowId)}</span>
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-slate-300/80 p-2 text-[11px] text-slate-500 dark:border-slate-700 dark:text-slate-400">
                Select target flow in settings →
              </div>
            )}
          </div>
        ) : (
          <div className="text-slate-800 dark:text-slate-200">
            {truncate(compactText(config.message, 'Send a friendly message to the customer.'), 88)}
          </div>
        )}
      </div>
    </NodeShell>
  );
}

/** Purpose: render a conditional branch node. Why: both true and fallback routes need visible, connectable handles. */
export function ConditionNode({ data, selected, id }) {
  const config = data?.config || {};
  const isAbSplit = data?.libraryKey === 'ab_split' || config.uiNodeKey === 'ab_split';

  if (isAbSplit) {
    const ratioA = Number.isFinite(Number(config.ratioA)) ? Number(config.ratioA) : 50;
    const ratioB = 100 - ratioA;
    const saveVar = config.saveToVariable || 'ab_variant';

    return (
      <NodeShell
        type="condition"
        selected={selected}
        data={{ ...data, id, nodeId: id }}
        eyebrowText="Logic · A/B Split Test"
        badgeText="A/B Split"
        allowOutgoing={false}
        hideOutgoing
      >
        <div className="space-y-2.5">
          <div className="flex items-center justify-between rounded-lg border border-purple-200/80 bg-purple-50/60 px-2.5 py-1 text-[11px] font-semibold text-purple-900 dark:border-purple-500/20 dark:bg-purple-950/30 dark:text-purple-200">
            <span>Traffic Distribution</span>
            <span className="font-mono font-bold text-purple-700 dark:text-purple-300">{ratioA}% / {ratioB}%</span>
          </div>

          {/* Path A */}
          <div className="group/patha relative mr-[-10px] rounded-xl border border-sky-300/90 bg-sky-50/80 p-2.5 pr-7 text-xs text-sky-950 shadow-xs transition-all duration-150 hover:-translate-y-px hover:shadow-md dark:border-sky-500/30 dark:bg-sky-950/30 dark:text-sky-200">
            <div className="flex items-center justify-between">
              <span className="rounded-md bg-sky-200/90 px-1.5 py-0.5 text-[10px] font-black uppercase text-sky-900 dark:bg-sky-500/30 dark:text-sky-100">
                Path A
              </span>
              <span className="font-mono text-[11px] font-bold text-sky-700 dark:text-sky-300">
                {ratioA}% traffic
              </span>
            </div>
            <Handle
              id="path_a"
              type="source"
              position={Position.Right}
              title="Drag or click to connect Path A"
              onClick={(event) => {
                event.stopPropagation();
                data?.onOpenNodePicker?.({ sourceNodeId: id, sourceHandle: 'path_a' });
              }}
              className="nodrag nopan !right-0 !z-30 !h-[17px] !w-[17px] !translate-x-1/2 !rounded-full !border-[3px] !border-sky-500 !bg-white !opacity-100 !shadow-[0_2px_8px_rgba(14,165,233,0.4)] !transition-transform hover:!scale-125 dark:!border-sky-400 dark:!bg-slate-900"
            />
          </div>

          {/* Path B */}
          <div className="group/pathb relative mr-[-10px] rounded-xl border border-pink-300/90 bg-pink-50/80 p-2.5 pr-7 text-xs text-pink-950 shadow-xs transition-all duration-150 hover:-translate-y-px hover:shadow-md dark:border-pink-500/30 dark:bg-pink-950/30 dark:text-pink-200">
            <div className="flex items-center justify-between">
              <span className="rounded-md bg-pink-200/90 px-1.5 py-0.5 text-[10px] font-black uppercase text-pink-900 dark:bg-pink-500/30 dark:text-pink-100">
                Path B
              </span>
              <span className="font-mono text-[11px] font-bold text-pink-700 dark:text-pink-300">
                {ratioB}% traffic
              </span>
            </div>
            <Handle
              id="path_b"
              type="source"
              position={Position.Right}
              title="Drag or click to connect Path B"
              onClick={(event) => {
                event.stopPropagation();
                data?.onOpenNodePicker?.({ sourceNodeId: id, sourceHandle: 'path_b' });
              }}
              className="nodrag nopan !right-0 !z-30 !h-[17px] !w-[17px] !translate-x-1/2 !rounded-full !border-[3px] !border-pink-500 !bg-white !opacity-100 !shadow-[0_2px_8px_rgba(236,72,153,0.4)] !transition-transform hover:!scale-125 dark:!border-pink-400 dark:!bg-slate-900"
            />
          </div>

          <div className="flex items-center justify-between text-[10px] text-slate-500 dark:text-slate-400">
            <span>Stores variant:</span>
            <span className="font-mono font-bold text-slate-700 dark:text-slate-300">&#123;&#123;{saveVar}&#125;&#125;</span>
          </div>
        </div>
      </NodeShell>
    );
  }

  const conditions = Array.isArray(config.conditions) && config.conditions.length
    ? config.conditions
    : [{ conditionId: 'condition-0', variable: config.variable || '', operator: config.operator || 'equals', value: config.value ?? '' }];

  return (
    <NodeShell
      type="condition"
      selected={selected}
      data={{ ...data, id, nodeId: id }}
      eyebrowText="Logic · Conditional Branching"
      badgeText="Branching"
      allowOutgoing={false}
      hideOutgoing
    >
      <div className="space-y-2">
        {conditions.map((rule, index) => (
          <div
            key={rule.conditionId || index}
            className="group/cond relative mr-[-10px] rounded-xl border border-violet-200/90 bg-violet-50/80 p-2.5 pr-7 text-xs text-violet-900 shadow-xs transition-all duration-150 hover:-translate-y-px hover:shadow-md dark:border-violet-500/25 dark:bg-violet-950/30 dark:text-violet-200"
          >
            <div className="flex items-center gap-1.5 font-bold uppercase tracking-wider text-[10px] text-violet-700 dark:text-violet-300">
              <span className="rounded-md bg-violet-200/80 px-1.5 py-0.5 font-black text-violet-900 dark:bg-violet-500/20 dark:text-violet-200">
                {index === 0 ? 'IF' : 'ELSE IF'}
              </span>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-1 font-mono text-[11px]">
              <span className="rounded bg-white/90 px-1.5 py-0.5 font-bold text-violet-800 shadow-2xs dark:bg-slate-900 dark:text-violet-200">
                {truncate(rule.variable || 'variable', 14)}
              </span>
              <span className="text-[10px] font-semibold text-violet-600 dark:text-violet-400">
                {rule.operator || '=='}
              </span>
              <span className="rounded bg-white/90 px-1.5 py-0.5 text-slate-800 shadow-2xs dark:bg-slate-900 dark:text-slate-200">
                {truncate(String(rule.value ?? "''"), 14)}
              </span>
            </div>
            <Handle
              id={rule.conditionId || `condition-${index}`}
              type="source"
              position={Position.Right}
              title={`Drag to connect branch #${index + 1}`}
              onClick={(event) => {
                event.stopPropagation();
                data?.onOpenNodePicker?.({ sourceNodeId: id, sourceHandle: rule.conditionId || `condition-${index}` });
              }}
              className="nodrag nopan !right-0 !z-30 !h-[17px] !w-[17px] !translate-x-1/2 !rounded-full !border-[3px] !border-violet-600 !bg-white !opacity-100 !shadow-[0_2px_8px_rgba(139,92,246,0.4)] !transition-transform hover:!scale-125 dark:!border-violet-400 dark:!bg-slate-900"
            />
          </div>
        ))}

        <div className="group/else relative mr-[-10px] rounded-xl border border-rose-200/90 bg-rose-50/70 p-2.5 pr-7 text-xs text-rose-800 shadow-xs transition-all duration-150 hover:-translate-y-px hover:shadow-md dark:border-rose-500/25 dark:bg-rose-950/30 dark:text-rose-200">
          <div className="flex items-center gap-1.5">
            <span className="rounded-md bg-rose-200/80 px-1.5 py-0.5 text-[10px] font-black text-rose-900 dark:bg-rose-500/20 dark:text-rose-200">
              ELSE
            </span>
            <span className="text-[10px] font-medium opacity-80">Default fallback path</span>
          </div>
          <Handle
            id="condition-else"
            type="source"
            position={Position.Right}
            title="Click or drag to connect fallback ELSE branch"
            onClick={(event) => {
              event.stopPropagation();
              data?.onOpenNodePicker?.({ sourceNodeId: id, sourceHandle: 'condition-else' });
            }}
            className="nodrag nopan !right-0 !z-30 !h-[17px] !w-[17px] !translate-x-1/2 !rounded-full !border-[3px] !border-rose-600 !bg-white !opacity-100 !shadow-[0_2px_8px_rgba(244,63,94,0.4)] !transition-transform hover:!scale-125 dark:!border-rose-400 dark:!bg-slate-900"
          />
        </div>
      </div>
    </NodeShell>
  );
}

/** Purpose: render a user-input prompt node. Why: captured answers and their validation/variable destination should be visible in the flow. */
export function InputNode({ data, selected, id }) {
  const config = data?.config || {};
  const isQuickReplyNode = data?.libraryKey === 'quick_replies' || config.validation === 'quick_reply';
  const typeMeta = getInputTypeMeta(data?.libraryKey, config.validation);
  const TypeIcon = typeMeta.icon;

  return (
    <NodeShell
      type="input"
      selected={selected}
      data={{ ...data, id, nodeId: id }}
      eyebrowText={`Prompt · ${typeMeta.label}`}
      badgeText={typeMeta.label}
      allowOutgoing={!isQuickReplyNode}
      hideOutgoing={isQuickReplyNode}
    >
      <div className={['min-h-[105px] space-y-3 rounded-xl border p-3.5 text-[13px]', legacyNodeTheme.input.surface].join(' ')}>
        {/* Input Validation Badge */}
        <div className="flex items-center justify-between gap-1">
          <span className={['inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider', typeMeta.badge].join(' ')}>
            <TypeIcon sx={{ fontSize: 13 }} />
            {typeMeta.label}
          </span>
          {config.required ? (
            <span className="text-[9px] font-bold text-amber-700 dark:text-amber-300">Required</span>
          ) : null}
        </div>

        {/* Prompt Card */}
        <div className="rounded-lg border border-slate-200/80 bg-white/90 p-2.5 font-medium leading-relaxed text-slate-900 shadow-2xs dark:border-white/10 dark:bg-slate-900/80 dark:text-slate-100">
          {config.localizationKey ? `🌐 ${config.localizationKey}` : truncate(config.prompt || 'Ask the user a question to collect input.', 96)}
        </div>

        {/* Variable Storage Box */}
        <div className="flex items-center justify-between rounded-lg border border-amber-200/70 bg-amber-50/70 px-2.5 py-1.5 text-[11px] text-amber-900 dark:border-amber-500/20 dark:bg-amber-950/30 dark:text-amber-200">
          <span className="flex items-center gap-1 font-medium opacity-85">
            <CodeRoundedIcon sx={{ fontSize: 13 }} />
            Stores in:
          </span>
          <span className="font-mono font-bold text-amber-800 dark:text-amber-200">
            {`{{${config.variable || config.variableName || 'user_input'}}}`}
          </span>
        </div>

        {/* If quick reply question, show options */}
        {isQuickReplyNode ? (
          <div className="pt-1">
            <QuickReplyButtons
              options={config.options || ['Option 1', 'Option 2']}
              onOpenNodePicker={data?.onOpenNodePicker}
              sourceNodeId={id}
              showFallback
              fallbackConfigured={data?.quickReplyFallbackConfigured}
              connectedSourceHandles={data?.connectedSourceHandles}
              onBranchHover={data?.onBranchHover}
              onGoToBranch={data?.onGoToBranch}
            />
          </div>
        ) : null}
      </div>
    </NodeShell>
  );
}

/** Purpose: render API/custom-function execution nodes. Why: authors need to see request purpose and success/failure connections in context. */
export function ApiCallNode({ data, selected, id }) {
  const request = data?.config?.asyncRequest || {};
  const isFunction = data?.libraryKey === 'function' || data?.category === 'custom_function';
  const isDatabase = data?.libraryKey === 'database';
  const functionName = data?.config?.functionDefinitionName || 'Select a saved function';
  const databaseOperation = data?.config?.customStorage?.operation || 'create';
  const databaseAction = databaseOperation === 'find_many'
    ? 'Search records'
    : databaseOperation === 'update'
      ? 'Search and update'
      : 'Insert record';
  const databaseTable = data?.config?.customStorage?.tableId ? 'Custom table selected' : 'Select a custom table';
  const httpMethodMeta = getHttpMethodMeta(request.method || 'POST');

  return (
    <NodeShell
      type="api_call"
      selected={selected}
      data={{ ...data, id, nodeId: id }}
      compact={isFunction}
      eyebrowText={isFunction ? 'Saved function' : isDatabase ? 'Database · Storage' : 'Integration · REST API'}
      badgeText={isFunction ? 'Function' : isDatabase ? 'Database' : 'REST API'}
      allowOutgoing={isFunction}
      hideOutgoing={!isFunction}
    >
      <div className={[
        isFunction
          ? 'space-y-2 rounded-xl border border-cyan-200/80 bg-gradient-to-br from-cyan-50/80 to-white/90 p-2.5 text-[12px] dark:border-cyan-500/20 dark:from-cyan-950/30 dark:to-slate-900/80'
          : ['min-h-[105px] space-y-2.5 rounded-xl border p-3.5 text-[13px]', legacyNodeTheme.api_call.surface].join(' '),
      ].join(' ')}>
        {isFunction ? (
          <>
            <div className="flex min-w-0 items-center gap-2">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-cyan-200/80 bg-white/80 text-cyan-700 dark:border-cyan-500/25 dark:bg-slate-900/70 dark:text-cyan-300">
                <CodeRoundedIcon sx={{ fontSize: 15 }} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-[9px] font-bold uppercase tracking-[0.12em] text-cyan-700/75 dark:text-cyan-300/75">Run function</div>
                <div className="truncate font-mono text-[11px] font-bold text-cyan-950 dark:text-cyan-100" title={functionName}>
                  {truncate(functionName, 30)}
                </div>
              </div>
            </div>
            {data?.config?.outputVariable ? (
              <div className="flex min-w-0 items-center gap-1.5 border-t border-cyan-200/60 pt-1.5 text-[10px] dark:border-cyan-500/20">
                <span className="shrink-0 text-slate-500 dark:text-slate-400">Returns to</span>
                <span className="truncate rounded-md bg-white/70 px-1.5 py-0.5 font-mono font-semibold text-cyan-800 dark:bg-slate-900/60 dark:text-cyan-200" title={data.config.outputVariable}>
                  {data.config.outputVariable}
                </span>
              </div>
            ) : null}
          </>
        ) : isDatabase ? (
          <>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1 font-bold text-cyan-800 dark:text-cyan-200">
                <StorageRoundedIcon sx={{ fontSize: 16 }} />
                <span>{databaseAction}</span>
              </span>
              <span className="rounded-md border border-cyan-200 bg-cyan-100/70 px-1.5 py-0.5 text-[9px] font-bold uppercase text-cyan-800 dark:border-cyan-500/30 dark:bg-cyan-500/20 dark:text-cyan-300">
                {databaseOperation}
              </span>
            </div>
            <div className="rounded-lg border border-cyan-200/80 bg-white/90 px-2.5 py-1.5 text-[11px] font-medium text-slate-700 shadow-2xs dark:border-white/10 dark:bg-slate-900/80 dark:text-slate-200">
              {databaseTable}
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <span className={['rounded-md border px-2 py-0.5 font-mono text-[10px] font-black', httpMethodMeta.badge].join(' ')}>
                {httpMethodMeta.method}
              </span>
              <span className="truncate font-mono text-[11px] font-bold text-slate-700 dark:text-slate-300">
                {truncate(request.url || 'https://api.example.com/endpoint', 26)}
              </span>
            </div>
          </>
        )}

        {/* Success and Failure tracks for API and Database nodes */}
        {!isFunction ? (
          <div className="space-y-2 pt-1">
            <div className="relative mr-[-10px] flex items-center justify-between rounded-xl border border-emerald-200/90 bg-emerald-50/80 py-2 pl-3 pr-7 text-[11px] font-bold text-emerald-800 shadow-xs transition-all duration-150 hover:-translate-y-px hover:shadow-md dark:border-emerald-500/25 dark:bg-emerald-950/30 dark:text-emerald-200">
              <span className="flex items-center gap-1.5">
                <CheckRoundedIcon sx={{ fontSize: 13 }} />
                Success (2xx)
              </span>
              <span className="text-[9px] opacity-60">Path</span>
              <Handle
                id="success"
                type="source"
                position={Position.Right}
                title="Drag to connect success path, or click to add next step"
                onClick={(event) => {
                  event.stopPropagation();
                  data?.onOpenNodePicker?.({ sourceNodeId: id, sourceHandle: 'success' });
                }}
                className="nodrag nopan !right-0 !z-30 !h-[17px] !w-[17px] !translate-x-1/2 !rounded-full !border-[3px] !border-emerald-600 !bg-white !opacity-100 !shadow-[0_2px_8px_rgba(16,185,129,0.4)] !transition-transform hover:!scale-125 dark:!border-emerald-400 dark:!bg-slate-900"
              />
            </div>
            <div className="relative mr-[-10px] flex items-center justify-between rounded-xl border border-rose-200/90 bg-rose-50/80 py-2 pl-3 pr-7 text-[11px] font-bold text-rose-800 shadow-xs transition-all duration-150 hover:-translate-y-px hover:shadow-md dark:border-rose-500/25 dark:bg-rose-950/30 dark:text-rose-200">
              <span className="flex items-center gap-1.5">
                <ErrorOutlineRoundedIcon sx={{ fontSize: 13 }} />
                Failure (4xx / 5xx / timeout)
              </span>
              <span className="text-[9px] opacity-60">Path</span>
              <Handle
                id="failure"
                type="source"
                position={Position.Right}
                title="Drag to connect failure/timeout path, or click to add next step"
                onClick={(event) => {
                  event.stopPropagation();
                  data?.onOpenNodePicker?.({ sourceNodeId: id, sourceHandle: 'failure' });
                }}
                className="nodrag nopan !right-0 !z-30 !h-[17px] !w-[17px] !translate-x-1/2 !rounded-full !border-[3px] !border-rose-600 !bg-white !opacity-100 !shadow-[0_2px_8px_rgba(244,63,94,0.4)] !transition-transform hover:!scale-125 dark:!border-rose-400 dark:!bg-slate-900"
              />
            </div>
          </div>
        ) : null}
      </div>
    </NodeShell>
  );
}

export const nodeTypes = {
  start: StartNode,
  end: EndNode,
  message: MessageNode,
  condition: ConditionNode,
  input: InputNode,
  api_call: ApiCallNode,
};
